import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useUser } from '../../hooks/useUser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGoogleLocationsGeocode,
  createStoreApiV2,
  updateStoreApi,
  deleteStore,
} from '../../utils/apis';
import {
  DotsThreeVertical,
  PencilSimple,
  TrashSimple,
} from '@phosphor-icons/react';
import { useState, useCallback } from 'react';
import StoreDrawer from '../UserJourny/FirstUserJourney/StoreDrawer';
import { ICreateStoreV2 } from '../../schemas/apis/interface';
import { debounce } from 'lodash';
import { useAlert } from '../../hooks/useAlert';
import NoDataFallback from '../common/NoDataFallback';

interface StoresProps {
  storesData: any;
  storesListLoading: boolean;
  brandsList: any;
  onEditStore: (store: any) => void;
  onStoreSelect?: (store: any) => void;
  onAddStoreHours?: (store: any) => void;
  onAddStoreMenu: (store: any) => void;
  storesListError?: any;
  isStoresListError?: boolean;
  paginationModel: { page: number; pageSize: number };
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
}

const Stores = ({
  storesData,
  storesListLoading,
  brandsList,
  onEditStore,
  onStoreSelect,
  onAddStoreHours,
  onAddStoreMenu,
  storesListError,
  isStoresListError,
  paginationModel,
  onPaginationModelChange,
}: StoresProps) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();

  // Add states for menu and drawer
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [storeDrawerOpen, setStoreDrawerOpen] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [selectionMade, setSelectionMade] = useState<boolean>(false);

  // Update the store state to match StoreDetails.tsx
  const [store, setStore] = useState<ICreateStoreV2>({
    friendly_name: '',
    poc_name: '',
    poc_email: '',
    poc_phone: '',
    poc_role: '',
    mode_of_operation: ['delivery', 'take_away'],
    brand_ids: [],
    street_address: '',
    zip_code: '',
    city: '',
    country: '',
    state_province: '',
    business_id: 0,
    is_store_closed: false,
    ordering_enabled: true,
  });

  // Add new state for setup popover
  const [setupAnchorEl, setSetupAnchorEl] = useState<number | null>(null);

  // Add the debounced geocode function

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGeocode = useCallback(
    debounce(async (location: string) => {
      if (!location.trim()) return;

      try {
        const geocodeResponse = await getGoogleLocationsGeocode({
          query: location,
        });

        const geocodeData = geocodeResponse.data.results[0];
        const addressComponents = geocodeData.address_components;

        const getComponent = (type: string, short = false) => {
          const component = addressComponents.find((comp: any) =>
            comp.types.includes(type)
          );
          return component
            ? short
              ? component.short_name
              : component.long_name
            : '';
        };

        setStore(prev => ({
          ...prev,
          street_address: geocodeData.formatted_address || location,
          city:
            getComponent('locality') ||
            getComponent('administrative_area_level_2'),
          state_province: getComponent('administrative_area_level_1'),
          country: getComponent('country'),
          zip_code: getComponent('postal_code'),
        }));
      } catch (error) {
        console.error('Error fetching geocode data:', error);
      }
    }, 500),
    []
  );

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, store: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedStore(store);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Update handleLocationChange
  const handleLocationChange = (location: string) => {
    setLocationInput(location);

    if (location.length > locationInput.length + 10 || selectionMade) {
      setSelectionMade(true);
      debouncedGeocode(location);
    }

    if (location.length < 3) {
      setSelectionMade(false);
    }
  };

  // Update handleEdit
  const handleEdit = () => {
    if (selectedStore) {
      onEditStore(selectedStore);
      handleMenuClose();
    }
  };

  // Update handleStoreChange to use the correct type
  const handleStoreChange = (updatedStore: ICreateStoreV2) => {
    setStore(updatedStore);
  };

  // Add mutations
  const { mutateAsync: createStore, isPending: createStorePending } =
    useMutation({
      mutationFn: ({ formData }: { formData: ICreateStoreV2 }) =>
        createStoreApiV2(formData, user?.tenantName || ''),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        showAlert('Store saved successfully', 'success');
        setStoreDrawerOpen(false);
      },
      onError: () => {
        showAlert('Failed to save store', 'error');
        setStoreDrawerOpen(false);
      },
    });

  const { mutateAsync: updateStore, isPending: updateStorePending } =
    useMutation({
      mutationFn: ({
        formData,
        storeId,
      }: {
        formData: ICreateStoreV2;
        storeId: string;
      }) => updateStoreApi(formData, user?.tenantName || '', storeId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        showAlert('Store updated successfully', 'success');
        setStoreDrawerOpen(false);
      },
      onError: () => {
        showAlert('Failed to update store', 'error');
        setStoreDrawerOpen(false);
      },
    });

  // Add the mutation for delete store
  const { mutateAsync: deleteStoreAsync } = useMutation({
    mutationFn: (storeId: string) =>
      deleteStore(storeId, user?.tenantName || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      showAlert('Store deleted successfully', 'success');
      handleMenuClose();
    },
    onError: () => {
      showAlert('Failed to delete store', 'error');
      handleMenuClose();
    },
  });

  // Update handleDelete function
  const handleDelete = async () => {
    if (selectedStore?.id) {
      try {
        await deleteStoreAsync(selectedStore.id.toString());
      } catch (error) {
        console.error('Error deleting store:', error);
      }
    }
  };

  // Update handleStoreSave
  const handleStoreSave = async (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent
  ) => {
    e.preventDefault();

    // Process any pending location changes
    if (locationInput && !selectionMade) {
      debouncedGeocode(locationInput);
    }

    // Wait for any pending geocode operations
    await new Promise(resolve => setTimeout(resolve, 600));

    const sanitizedData: ICreateStoreV2 = {
      ...store,
      street_address: store.street_address || locationInput,
      zip_code: store.zip_code || '',
      city: store.city || '',
      country: store.country || '',
      state_province: store.state_province || '',
      business_id: store.business_id || 0,
      mode_of_operation: store.mode_of_operation?.length
        ? store.mode_of_operation
        : ['delivery', 'take_away'],
      ordering_enabled: store.ordering_enabled ?? true,
      is_store_closed: store.is_store_closed ?? false,
    };

    try {
      if (selectedStore?.id) {
        await updateStore({
          formData: sanitizedData,
          storeId: selectedStore.id.toString(),
        });
      } else {
        await createStore({
          formData: sanitizedData,
        });
      }
    } catch (error) {
      console.error('Error saving store:', error);
    }
  };

  const handleStoreClick = (store: any) => {
    if (onStoreSelect) {
      onStoreSelect(store);
    }
  };

  const handleSetupClose = () => {
    setSetupAnchorEl(null);
    setSelectedStore(null);
  };

  // Update the handleOpenMenuConfig function
  const handleOpenMenuConfig = (store: any) => {
    handleSetupClose();
    onAddStoreMenu(store);
  };

  // Extract stores array and total count from the new API structure
  const storesList = storesData?.data?.stores || [];
  const totalCount = storesData?.data?.total || 0;

  const columns: GridColDef[] = [
    {
      field: 'friendly_name',
      headerName: 'Name',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => handleStoreClick(params.row)}
        >
          <Tooltip
            title="View details"
            sx={{
              '& .MuiTooltip-tooltip': {
                backgroundColor: '#FFF',
                color: '#2B2D3F',
                fontSize: '14px',
              },
              width: '100%',
            }}
            placement="top"
          >
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      headerClassName: 'data-grid-header',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'brand_ids',
      headerName: 'Brands At Outlet',
      flex: 2,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {params.value?.map((brandId: string) => {
            const brand = brandsList?.data?.data.find(
              (b: any) => b.id === brandId
            );
            return brand ? (
              <Chip
                key={brand.id}
                label={brand.name}
                sx={{
                  height: '24px',
                  fontSize: '12px',
                  bgcolor: '#5A4CE61A',
                  borderRadius: '6px',
                  gap: '10px',
                  textAlign: 'center',
                }}
              />
            ) : null;
          })}
        </Box>
      ),
    },
    {
      field: 'poc_name',
      headerName: 'Contact Person',
      flex: 1,
      headerClassName: 'data-grid-header',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      headerClassName: 'data-grid-header',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: '100%',
              pr: 2,
            }}
          >
            {!params.row.is_menu_configured ||
            !params.row.opening_hours?.length ? (
              <Button
                size="small"
                onClick={e => {
                  setSelectedStore(params.row);
                  // @ts-expect-error
                  setSetupAnchorEl(e.currentTarget);
                }}
                sx={{
                  color: '#E74C3C',
                  fontSize: '14px',
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                // endIcon={<CaretDown size={16} />}
              >
                Setup Required
              </Button>
            ) : (
              <IconButton
                onClick={e => handleMenuOpen(e, params.row)}
                size="small"
              >
                <DotsThreeVertical size={16} />
              </IconButton>
            )}
          </Box>
        </Box>
      ),
    },
  ];

  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%', height: '100%' }}>
      {[...Array(5)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 0.5fr 2fr 1fr 1fr',
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            alignItems: 'center',
          }}
        >
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="60%" height={24} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: '6px' }}
            />
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: '6px' }}
            />
          </Box>
          <Skeleton variant="text" width="70%" height={24} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="text" width={100} height={24} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  if (isStoresListError && storesListError && storesListLoading) {
    return <NoDataFallback httpStatusCode={storesListError?.status} />;
  }

  if ((!storesList || storesList?.length === 0) && !storesListLoading) {
    return (
      <NoDataFallback
        message="No stores available"
        description="Looks like there are no stores to display right now. Once you create a store, it’ll show up here"
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          border: '1px solid rgba(224, 224, 224, 1)',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {storesListLoading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                bgcolor: '#F8F9FC',
                display: 'grid',
                gridTemplateColumns: '1.5fr 0.5fr 2fr 1fr 1fr',
                gap: 2,
                p: 2,
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Name
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                ID
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Brands At Outlet
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                POC Name
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                  textAlign: 'right',
                }}
              >
                Action
              </Typography>
            </Box>
            <LoadingSkeleton />
          </Box>
        ) : (
          <DataGrid
            rows={storesList || []}
            columns={columns}
            loading={storesListLoading}
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[10, 20, 50, 75, 100]}
            // Add server-side pagination support
            paginationMode="server"
            rowCount={totalCount}
            getRowHeight={params => {
              return setupAnchorEl === params.id ? 300 : 52;
            }}
            sx={{
              flex: 1,
              '& .data-grid-header': {
                bgcolor: '#F8F9FC',
                color: 'custom.textBlue',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 16px',
              },
              '& .MuiDataGrid-cell': {
                padding: '12px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                '&:focus, &:focus-within': {
                  outline: 'none !important',
                },
              },
              border: 'none',
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none !important',
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  No stores available
                </Box>
              ),
            }}
          />
        )}
      </Box>

      {/* Add Menu component */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            fontSize: '14px',
            gap: 1,
            py: 1,
            px: 2,
          }}
        >
          <PencilSimple size={16} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            fontSize: '14px',
            gap: 1,
            py: 1,
            px: 2,
            color: '#E74C3C',
          }}
        >
          <TrashSimple size={16} />
          Delete
        </MenuItem>
      </Menu>

      <Menu
        // @ts-expect-error
        anchorEl={setupAnchorEl}
        open={Boolean(setupAnchorEl)}
        onClose={handleSetupClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            mt: 1,
            minWidth: '800px',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#2B2D3F',
            p: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          Complete the following steps to complete store setup
        </Typography>
        {selectedStore && (
          <>
            {!selectedStore.opening_hours?.length && (
              <MenuItem
                sx={{
                  fontSize: '14px',
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                >
                  Create opening hours for the store
                </Typography>
                <Button
                  variant="text"
                  onClick={() => {
                    setSetupAnchorEl(null);
                    onAddStoreHours?.(selectedStore);
                  }}
                  sx={{
                    color: '#5A4CE6',
                    textTransform: 'none',
                  }}
                >
                  Add Opening Hours
                </Button>
              </MenuItem>
            )}
            {!selectedStore.is_menu_configured && (
              <MenuItem
                sx={{
                  fontSize: '14px',
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                >
                  Import menu for the store
                </Typography>
                <Button
                  variant="text"
                  onClick={() => handleOpenMenuConfig(selectedStore)}
                  sx={{
                    color: '#5A4CE6',
                    textTransform: 'none',
                  }}
                >
                  Import Menu
                </Button>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Add StoreDrawer component */}
      <StoreDrawer
        open={storeDrawerOpen}
        onClose={() => {
          setStoreDrawerOpen(false);
          setSelectionMade(false);
          setLocationInput('');
        }}
        store={store}
        onStoreChange={handleStoreChange}
        onSave={handleStoreSave}
        brandsList={brandsList}
        isPending={createStorePending || updateStorePending}
        handleLocationChange={handleLocationChange}
        locationInput={locationInput || store.street_address}
      />
    </Box>
  );
};

export default Stores;
